import type {
  AiKeySummary,
  BulkAddProxyResult,
  BulkProxyActionResult,
  CheckerCredentialsInput,
  CheckerCredentialsStatus,
  CreateAiKeyInput,
  CreateJobInput,
  CreateProxyEndpointInput,
  CreatePromptTemplateInput,
  CreateSharedTokenInput,
  CreateUserInput,
  JobConfig,
  LegacyImportPreview,
  ProxyConfigPublic,
  ProxyEndpointPublic,
  ProxyTestResult,
  PromptTemplateRecord,
  RunDeployResult,
  SharedTokenLogItem,
  SharedTokenSummary,
  UpdateJobInput,
  UpdateAiKeyInput,
  UpdateProxyEndpointInput,
  UpdatePromptTemplateInput,
  UpdateSharedTokenInput,
  UpdateUserInput,
  UserPublic,
  ValidationReport,
} from '@generator/shared-types';

const API_BASE = '/api';

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly details?: unknown,
  ) {
    super(message);
  }
}

async function request<T>(
  path: string,
  init: RequestInit = {},
  parseJson = true,
): Promise<T> {
  const hasBody = init.body != null;
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    credentials: 'include',
    headers: {
      ...(hasBody ? { 'Content-Type': 'application/json' } : {}),
      ...(init.headers ?? {}),
    },
  });
  if (!res.ok) {
    let body: unknown;
    try {
      body = await res.json();
    } catch {
      body = await res.text().catch(() => '');
    }
    const message =
      typeof body === 'object' && body && 'message' in body
        ? String((body as { message: unknown }).message)
        : `HTTP ${res.status}`;
    // Global 401 handling: redirect to /login. Exception: /auth/me probe so
    // the initial unauthenticated check doesn't trigger a redirect loop.
    if (
      res.status === 401 &&
      !path.startsWith('/auth/') &&
      typeof window !== 'undefined' &&
      window.location.pathname !== '/login'
    ) {
      window.location.assign('/login');
    }
    throw new ApiError(message, res.status, body);
  }
  if (!parseJson || res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export interface JobListItem {
  id: string;
  name: string;
  domain: string;
  brand: string;
  topic: string;
  region: string;
  language: string;
  articleCount: number;
  status: string;
  runCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface RunSummary {
  id: string;
  jobId: string;
  runNumber: number;
  status: string;
  totalArticles: number;
  name: string;
  domain: string;
  completed: number;
  failed: number;
  startedAt: string | null;
  finishedAt: string | null;
  cancelledAt: string | null;
  pausedAt: string | null;
  pausedMs: number;
  errorMessage: string | null;
  outputDir: string;
  createdAt: string;
  totalInputTokens: number;
  totalOutputTokens: number;
  totalCachedTokens: number;
  costUsd: number;
  downloaded: boolean;
  /** Set once the run was pushed to Checker. Global — not per-user like `downloaded`. */
  deployedAt: string | null;
  checkerCampaignId: string | null;
}

export interface PaginatedRuns {
  items: RunSummary[];
  total: number;
  page: number;
  pageSize: number;
}

export interface RunArticle {
  id: string;
  index: number;
  keyword: string;
  title: string | null;
  filename: string | null;
  status: string;
  retries: number;
  wordCount: number | null;
  errorMessage: string | null;
  flagged: boolean;
  flagReason: string | null;
}

export interface RunDetail extends RunSummary {
  articles: RunArticle[];
  config: Record<string, unknown> | null;
  hasConfigOverride: boolean;
  flaggedCount: number;
  /** 'shared_exhausted' = auto-paused by a dry shared pool (auto-resume revives it); 'budget_exceeded' = the run reached its max cost per task; null = manual pause. */
  pauseReason: string | null;
}

export interface PaginatedArticles {
  items: RunArticle[];
  total: number;
  page: number;
  pageSize: number;
}

export interface RunLogEntry {
  id: string;
  level: string;
  articleId: string | null;
  step: string | null;
  message: string;
  meta: unknown;
  createdAt: string;
}

export interface OutputFileMeta {
  filename: string;
  bytes: number;
  modifiedAt: string;
}

export interface AdminJobListItem {
  id: string;
  name: string;
  domain: string;
  ownerEmail: string;
  ownerId: string;
  status: string;
  articleCount: number;
  runCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface AdminRunListItem {
  id: string;
  jobId: string;
  runNumber: number;
  status: string;
  totalArticles: number;
  name: string;
  domain: string;
  completed: number;
  failed: number;
  costUsd: number;
  startedAt: string | null;
  finishedAt: string | null;
  createdAt: string;
  downloaded: boolean;
}

export interface WindowSums {
  today: number;
  week: number;
  month: number;
}

/** Spend in USD per window, plus how many tasks (runs) were launched in it. */
export interface CostSummary extends WindowSums {
  tasks: WindowSums;
}

export interface AdminUserCost extends CostSummary {
  userId: string;
  email: string;
}

export interface AdminCostSummary {
  users: AdminUserCost[];
  /** Spend of accounts that were deleted since — kept in the totals. */
  deletedAccounts: CostSummary;
  totals: CostSummary;
}

export const api = {
  jobs: {
    list: (filters: { status?: string; brand?: string; region?: string } = {}) => {
      const params = new URLSearchParams();
      if (filters.status) params.set('status', filters.status);
      if (filters.brand) params.set('brand', filters.brand);
      if (filters.region) params.set('region', filters.region);
      const qs = params.toString();
      return request<JobListItem[]>(`/jobs${qs ? `?${qs}` : ''}`);
    },
    get: (id: string) => request<JobConfig>(`/jobs/${id}`),
    create: (body: CreateJobInput) =>
      request<JobConfig>('/jobs', { method: 'POST', body: JSON.stringify(body) }),
    update: (id: string, body: UpdateJobInput) =>
      request<JobConfig>(`/jobs/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    remove: (id: string) =>
      request<void>(`/jobs/${id}`, { method: 'DELETE' }, false),
    run: (id: string) =>
      request<{ runId: string }>(`/jobs/${id}/run`, { method: 'POST' }),
    listRuns: (
      id: string,
      params: { search?: string; page?: number; pageSize?: number } = {},
    ) => {
      const qs = new URLSearchParams();
      if (params.search) qs.set('search', params.search);
      if (params.page) qs.set('page', String(params.page));
      if (params.pageSize) qs.set('pageSize', String(params.pageSize));
      const q = qs.toString();
      return request<PaginatedRuns>(`/jobs/${id}/runs${q ? `?${q}` : ''}`);
    },
    getRun: (jobId: string, runId: string) =>
      request<RunDetail>(`/jobs/${jobId}/runs/${runId}`),
    cancel: (jobId: string, runId: string) =>
      request<{ ok: boolean }>(`/jobs/${jobId}/runs/${runId}/cancel`, { method: 'POST' }),
    pause: (jobId: string, runId: string) =>
      request<{ ok: boolean }>(`/jobs/${jobId}/runs/${runId}/pause`, { method: 'POST' }),
    resume: (jobId: string, runId: string) =>
      request<{ ok: boolean }>(`/jobs/${jobId}/runs/${runId}/resume`, { method: 'POST' }),
    restartRun: (jobId: string, runId: string) =>
      request<{ ok: boolean }>(`/jobs/${jobId}/runs/${runId}/restart`, { method: 'POST' }),
    logs: (jobId: string, runId: string, opts: { limit?: number; level?: string } = {}) => {
      const params = new URLSearchParams();
      if (opts.limit) params.set('limit', String(opts.limit));
      if (opts.level) params.set('level', opts.level);
      const qs = params.toString();
      return request<RunLogEntry[]>(
        `/jobs/${jobId}/runs/${runId}/logs${qs ? `?${qs}` : ''}`,
      );
    },
    updateRunConfig: (jobId: string, runId: string, body: CreateJobInput) =>
      request<{ ok: boolean }>(
        `/jobs/${jobId}/runs/${runId}/config`,
        { method: 'PATCH', body: JSON.stringify(body) },
      ),
    retry: (jobId: string, runId: string, articleId: string) =>
      request<{ ok: boolean }>(
        `/jobs/${jobId}/runs/${runId}/articles/${articleId}/retry`,
        { method: 'POST' },
      ),
    backfillVideo: (jobId: string, runId: string, articleId: string) =>
      request<{ ok: boolean }>(
        `/jobs/${jobId}/runs/${runId}/articles/${articleId}/backfill-video`,
        { method: 'POST' },
      ),
    regenerateArticle: (jobId: string, runId: string, articleId: string) =>
      request<{ ok: boolean }>(
        `/jobs/${jobId}/runs/${runId}/articles/${articleId}/regenerate`,
        { method: 'POST' },
      ),
    regenerateFlagged: (jobId: string, runId: string) =>
      request<{ ok: boolean; queued: number }>(
        `/jobs/${jobId}/runs/${runId}/articles/regenerate-flagged`,
        { method: 'POST' },
      ),
    deleteRun: (jobId: string, runId: string) =>
      request<void>(`/jobs/${jobId}/runs/${runId}`, { method: 'DELETE' }, false),
    bulkDeleteRuns: (jobId: string, runIds: string[]) =>
      request<void>(
        `/jobs/${jobId}/runs/bulk-delete`,
        { method: 'POST', body: JSON.stringify({ runIds }) },
        false,
      ),
    listArticles: (
      jobId: string,
      runId: string,
      params: { search?: string; status?: string; page?: number; pageSize?: number } = {},
    ) => {
      const qs = new URLSearchParams();
      if (params.search) qs.set('search', params.search);
      if (params.status) qs.set('status', params.status);
      if (params.page) qs.set('page', String(params.page));
      if (params.pageSize) qs.set('pageSize', String(params.pageSize));
      const q = qs.toString();
      return request<PaginatedArticles>(`/jobs/${jobId}/runs/${runId}/articles${q ? `?${q}` : ''}`);
    },
    deleteArticle: (jobId: string, runId: string, articleId: string) =>
      request<void>(`/jobs/${jobId}/runs/${runId}/articles/${articleId}`, { method: 'DELETE' }, false),
    bulkDeleteArticles: (jobId: string, runId: string, articleIds: string[]) =>
      request<void>(
        `/jobs/${jobId}/runs/${runId}/articles/bulk-delete`,
        { method: 'POST', body: JSON.stringify({ articleIds }) },
        false,
      ),
    bulkRetryArticles: (jobId: string, runId: string, articleIds: string[]) =>
      request<{ ok: boolean; queued: number }>(
        `/jobs/${jobId}/runs/${runId}/articles/bulk-retry`,
        { method: 'POST', body: JSON.stringify({ articleIds }) },
      ),
  },
  checker: {
    credentials: () => request<CheckerCredentialsStatus>('/checker/credentials'),
    saveCredentials: (body: CheckerCredentialsInput) =>
      request<CheckerCredentialsStatus>('/checker/credentials', {
        method: 'PUT',
        body: JSON.stringify(body),
      }),
    disconnect: () => request<void>('/checker/credentials', { method: 'DELETE' }, false),
    deploy: (jobId: string, runId: string, name?: string) =>
      request<RunDeployResult>(`/jobs/${jobId}/runs/${runId}/deploy`, {
        method: 'POST',
        body: JSON.stringify(name ? { name } : {}),
      }),
  },
  outputs: {
    list: (jobId: string, runId: string) =>
      request<OutputFileMeta[]>(`/jobs/${jobId}/runs/${runId}/outputs`),
    fileUrl: (jobId: string, runId: string, filename: string) =>
      `${API_BASE}/jobs/${jobId}/runs/${runId}/outputs/${encodeURIComponent(filename)}`,
    zipUrl: (jobId: string, runId: string) =>
      `${API_BASE}/jobs/${jobId}/runs/${runId}/outputs/zip`,
  },
  templates: {
    list: () => request<PromptTemplateRecord[]>('/templates'),
    get: (id: string) => request<PromptTemplateRecord>(`/templates/${id}`),
    create: (body: CreatePromptTemplateInput) =>
      request<PromptTemplateRecord>('/templates', { method: 'POST', body: JSON.stringify(body) }),
    update: (id: string, body: UpdatePromptTemplateInput) =>
      request<PromptTemplateRecord>(`/templates/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    remove: (id: string) =>
      request<void>(`/templates/${id}`, { method: 'DELETE' }, false),
    validateMacros: (prompt: string) =>
      request<{ unknownMacros: string[] }>('/templates/validate-macros', {
        method: 'POST',
        body: JSON.stringify({ prompt }),
      }),
  },
  aiKeys: {
    list: () => request<AiKeySummary[]>('/ai-keys'),
    create: (body: CreateAiKeyInput) =>
      request<AiKeySummary>('/ai-keys', { method: 'POST', body: JSON.stringify(body) }),
    update: (id: string, body: UpdateAiKeyInput) =>
      request<AiKeySummary>(`/ai-keys/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    rotate: (id: string, apiKey: string) =>
      request<AiKeySummary>(`/ai-keys/${id}/rotate`, {
        method: 'PATCH',
        body: JSON.stringify({ apiKey }),
      }),
    remove: (id: string) =>
      request<void>(`/ai-keys/${id}`, { method: 'DELETE' }, false),
    test: (id: string, model?: string) =>
      request<{ ok: boolean; latencyMs: number; error?: string }>(`/ai-keys/${id}/test`, {
        method: 'POST',
        body: JSON.stringify({ model }),
      }),
  },
  validation: {
    article: (html: string, minWordCount = 1500) =>
      request<ValidationReport>('/validation/article', {
        method: 'POST',
        body: JSON.stringify({ html, minWordCount }),
      }),
  },
  legacyImport: {
    preview: (json: unknown) =>
      request<{ config: CreateJobInput; preview: LegacyImportPreview }>(
        '/legacy-import/preview',
        { method: 'POST', body: JSON.stringify(json) },
      ),
    commit: (json: unknown) =>
      request<{ jobId: string }>('/legacy-import/commit', {
        method: 'POST',
        body: JSON.stringify(json),
      }),
  },
  auth: {
    login: (email: string, password: string) =>
      request<{ user: UserPublic }>('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      }),
    logout: () => request<{ ok: boolean }>('/auth/logout', { method: 'POST' }),
    me: () => request<{ user: UserPublic }>('/auth/me'),
  },
  users: {
    list: () => request<UserPublic[]>('/users'),
    create: (body: CreateUserInput) =>
      request<UserPublic>('/users', { method: 'POST', body: JSON.stringify(body) }),
    update: (id: string, body: UpdateUserInput) =>
      request<UserPublic>(`/users/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    remove: (id: string) =>
      request<void>(`/users/${id}`, { method: 'DELETE' }, false),
  },
  proxySettings: {
    get: () => request<ProxyConfigPublic>('/proxy-settings'),
    setEnabled: (enabled: boolean) =>
      request<ProxyConfigPublic>('/proxy-settings', {
        method: 'PUT',
        body: JSON.stringify({ enabled }),
      }),
    setSharedAiEnabled: (enabled: boolean) =>
      request<ProxyConfigPublic>('/proxy-settings/shared-ai', {
        method: 'PUT',
        body: JSON.stringify({ enabled }),
      }),
    refreshSharedAiBindings: () =>
      request<ProxyConfigPublic>('/proxy-settings/shared-ai/refresh-bindings', {
        method: 'POST',
      }),
    addEndpoint: (body: CreateProxyEndpointInput) =>
      request<ProxyEndpointPublic>('/proxy-settings/endpoints', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    removeEndpointsBulk: (ids: string[]) =>
      request<BulkProxyActionResult>('/proxy-settings/endpoints/bulk-delete', {
        method: 'POST',
        body: JSON.stringify({ ids }),
      }),
    setEndpointsEnabledBulk: (ids: string[], enabled: boolean) =>
      request<BulkProxyActionResult>('/proxy-settings/endpoints/bulk-enabled', {
        method: 'POST',
        body: JSON.stringify({ ids, enabled }),
      }),
    addEndpointsBulk: (urls: string[]) =>
      request<BulkAddProxyResult>('/proxy-settings/endpoints/bulk', {
        method: 'POST',
        body: JSON.stringify({ urls }),
      }),
    updateEndpoint: (id: string, body: UpdateProxyEndpointInput) =>
      request<ProxyEndpointPublic>(`/proxy-settings/endpoints/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    removeEndpoint: (id: string) =>
      request<void>(`/proxy-settings/endpoints/${id}`, { method: 'DELETE' }, false),
    testEndpoint: (id: string) =>
      request<ProxyTestResult>(`/proxy-settings/endpoints/${id}/test`, { method: 'POST' }),
  },
  stats: {
    cost: () =>
      request<CostSummary>(`/stats/cost?tzOffsetMinutes=${new Date().getTimezoneOffset()}`),
  },
  admin: {
    listJobs: () => request<AdminJobListItem[]>('/admin/jobs'),
    costs: () =>
      request<AdminCostSummary>(
        `/admin/stats/cost?tzOffsetMinutes=${new Date().getTimezoneOffset()}`,
      ),
    listRuns: (jobId: string) =>
      request<AdminRunListItem[]>(`/admin/jobs/${jobId}/runs`),
    zipUrl: (jobId: string, runId: string) =>
      `${API_BASE}/admin/jobs/${jobId}/runs/${runId}/outputs/zip`,
    sharedTokens: {
      list: () => request<SharedTokenSummary[]>('/admin/shared-tokens'),
      create: (body: CreateSharedTokenInput) =>
        request<SharedTokenSummary>('/admin/shared-tokens', { method: 'POST', body: JSON.stringify(body) }),
      update: (id: string, body: UpdateSharedTokenInput) =>
        request<SharedTokenSummary>(`/admin/shared-tokens/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
      remove: (id: string) =>
        request<void>(`/admin/shared-tokens/${id}`, { method: 'DELETE' }, false),
      resetUsage: (id: string) =>
        request<void>(`/admin/shared-tokens/${id}/reset-usage`, { method: 'POST' }, false),
      logs: (opts: { limit?: number; level?: string } = {}) => {
        const qs = new URLSearchParams();
        if (opts.limit) qs.set('limit', String(opts.limit));
        if (opts.level) qs.set('level', opts.level);
        const q = qs.toString();
        return request<SharedTokenLogItem[]>(`/admin/shared-tokens/logs${q ? `?${q}` : ''}`);
      },
    },
  },
};

export function eventStreamUrl(jobId: string, runId: string): string {
  return `${API_BASE}/jobs/${jobId}/runs/${runId}/events`;
}

/** Why the deploy wants the connect dialog: never linked, or the saved login went stale. */
export type CheckerConnectPrompt = 'missing' | 'stale';

/**
 * Two deploy failures are prompts, not messages: the user has no Checker account
 * connected, or the stored one stopped working. Both are answered by the same
 * dialog, so the UI branches on them instead of rendering the text.
 *
 * Neither is a 401 on purpose — `request()` above redirects to /login on any 401
 * outside /auth/, and a stale *Checker* password must not sign the user out here.
 */
export function checkerConnectPrompt(err: unknown): CheckerConnectPrompt | null {
  if (
    !(err instanceof ApiError) ||
    err.status !== 409 ||
    typeof err.details !== 'object' ||
    err.details === null
  ) {
    return null;
  }
  const code = (err.details as { code?: unknown }).code;
  if (code === 'NO_CREDENTIALS') return 'missing';
  if (code === 'CHECKER_AUTH_FAILED') return 'stale';
  return null;
}

/**
 * The name Checker refused as a duplicate, or null when this failure is not a
 * name conflict. Returned by the server rather than re-derived here: it is
 * resolved from the run's config snapshot, and on a retry it is the user's own
 * edited string, which this page cannot reconstruct.
 */
export function checkerNameConflict(err: unknown): string | null {
  if (
    !(err instanceof ApiError) ||
    err.status !== 409 ||
    typeof err.details !== 'object' ||
    err.details === null
  ) {
    return null;
  }
  const { code, campaignName } = err.details as { code?: unknown; campaignName?: unknown };
  if (code !== 'CAMPAIGN_NAME_TAKEN') return null;
  // A blank name still opens the field — the API keeps that run eligible on purpose
  // (Job.domain defaults to '', so a job with no domain deploys under the empty
  // name), and it is the one that most needs somewhere to type a real one. Callers
  // must therefore test `!== null`, never truthiness.
  return typeof campaignName === 'string' ? campaignName : null;
}
